/**
 * Centralized logging utility with configurable log levels.
 *
 * Log levels (in order of verbosity):
 * - debug: Development debugging, verbose output
 * - info: General information, notable events
 * - warn: Warnings, potential issues
 * - error: Errors, failures
 *
 * Level resolution order (highest precedence first):
 * 1. Runtime override in `localStorage` (key `chk:logLevel`) — set via `setLevel`.
 * 2. `import.meta.env.VITE_LOG_LEVEL` (build-time).
 * 3. Default: 'warn' in production, 'debug' in development.
 *
 * Invalid values at any source are ignored and the next source is used.
 * The resolved level is cached after first use (so log calls don't re-read
 * `localStorage` every time); `setLevel`/`resetLevel` keep the cache in sync,
 * so they take effect immediately (no reload required).
 *
 * Errors are automatically reported to Sentry (if configured).
 *
 * @example
 * ```typescript
 * import { logger, setLevel } from '@/lib/logger';
 *
 * logger.debug('Fetching data...', { userId: '123' });
 * logger.info('User logged in');
 * logger.warn('Deprecated API called');
 * logger.error('Failed to save', error);
 *
 * setLevel('debug'); // runtime override, takes effect immediately
 * ```
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

/**
 * localStorage key for the runtime log-level override (module-internal).
 * Takes precedence over the build-time VITE_LOG_LEVEL.
 */
const LOG_LEVEL_STORAGE_KEY = "chk:logLevel";

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const isLogLevel = (value: unknown): value is LogLevel =>
  typeof value === "string" && value in LOG_LEVELS;

/**
 * Read the runtime override from localStorage, guarded against environments
 * where storage is unavailable (SSR, private mode, disabled storage).
 * Returns null when no valid override is present.
 */
const readStoredLevel = (): LogLevel | null => {
  try {
    if (typeof localStorage === "undefined") {
      return null;
    }
    const stored = localStorage.getItem(LOG_LEVEL_STORAGE_KEY);
    return isLogLevel(stored) ? stored : null;
  } catch {
    // Storage unavailable / access denied — fall through to env/default.
    return null;
  }
};

/**
 * Compute the active log level from scratch.
 * Resolution order: localStorage override → VITE_LOG_LEVEL → default.
 * Invalid values are ignored and the next source is used.
 */
const resolveConfiguredLevel = (): LogLevel => {
  const storedLevel = readStoredLevel();
  if (storedLevel) {
    return storedLevel;
  }

  const envLevel = import.meta.env.VITE_LOG_LEVEL;
  if (isLogLevel(envLevel)) {
    return envLevel;
  }

  // Default: 'warn' in production, 'debug' in development
  return import.meta.env.PROD ? "warn" : "debug";
};

/**
 * Lazily-computed cache of the resolved level. Computed once on first read
 * (avoiding a synchronous `localStorage` read on every `logger.*` call,
 * including suppressed ones), then kept in sync by `setLevel` (set to the new
 * level) and `resetLevel` (invalidated → recomputed on next read).
 */
let cachedLevel: LogLevel | null = null;

/**
 * Resolve the active log level, using the lazy cache.
 * The resolution order is computed once via {@link resolveConfiguredLevel};
 * subsequent calls return the cached value until `setLevel`/`resetLevel`
 * update or invalidate it.
 */
const getConfiguredLevel = (): LogLevel => {
  if (cachedLevel === null) {
    cachedLevel = resolveConfiguredLevel();
  }
  return cachedLevel;
};

const shouldLog = (level: LogLevel): boolean => {
  const configuredLevel = getConfiguredLevel();
  return LOG_LEVELS[level] >= LOG_LEVELS[configuredLevel];
};

const formatMessage = (level: LogLevel, message: string): string => {
  const timestamp = new Date().toISOString();
  return `[${timestamp}] [${level.toUpperCase()}] ${message}`;
};

/**
 * Set the runtime log-level override. Takes effect immediately for
 * subsequent log calls (on success the cached level is updated to the new
 * value, so no reload is required). Invalid levels are ignored.
 *
 * @param level - One of 'debug' | 'info' | 'warn' | 'error'.
 * @returns true if the override was applied, false otherwise.
 */
export const setLevel = (level: LogLevel): boolean => {
  if (!isLogLevel(level)) {
    return false;
  }
  try {
    if (typeof localStorage === "undefined") {
      return false;
    }
    localStorage.setItem(LOG_LEVEL_STORAGE_KEY, level);
    // Keep the lazy cache in sync so the new level is active immediately.
    cachedLevel = level;
    return true;
  } catch {
    return false;
  }
};

/**
 * Get the currently active log level (after applying the resolution order).
 */
export const getLevel = (): LogLevel => getConfiguredLevel();

/**
 * Clear the runtime override, falling back to VITE_LOG_LEVEL / default.
 * Invalidates the lazy cache so the level is recomputed on the next read.
 */
export const resetLevel = (): void => {
  try {
    if (typeof localStorage === "undefined") {
      return;
    }
    localStorage.removeItem(LOG_LEVEL_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  } finally {
    // Invalidate the cache regardless: next read recomputes from env/default.
    cachedLevel = null;
  }
};

/**
 * The ordered list of selectable log levels (most → least verbose).
 * Exposed for UI controls that let the user pick a level at runtime.
 */
export const LOG_LEVEL_OPTIONS: readonly LogLevel[] = ["debug", "info", "warn", "error"];

/**
 * Report error to Sentry if available.
 * This is a separate function to avoid importing Sentry in every file.
 */
const reportToSentry = (message: string, args: unknown[]): void => {
  // Dynamically check for Sentry to avoid hard dependency
  if (typeof window !== "undefined") {
    import("@sentry/react")
      .then((Sentry) => {
        // Check if Sentry is initialized
        if (Sentry.isInitialized()) {
          const error = args.find((arg) => arg instanceof Error);
          if (error instanceof Error) {
            Sentry.captureException(error, {
              extra: {
                message,
                args: args.filter((a) => !(a instanceof Error)),
              },
            });
          } else {
            Sentry.captureMessage(message, {
              level: "error",
              extra: { args },
            });
          }
        }
      })
      .catch(() => {
        // Sentry not available, silently ignore
      });
  }
};

/**
 * Logger instance with level-based filtering.
 * Only logs messages at or above the configured log level.
 */
export const logger = {
  /**
   * Debug level - verbose development logging.
   * Use for tracing, detailed state, temporary debugging.
   */
  debug: (message: string, ...args: unknown[]): void => {
    if (shouldLog("debug")) {
      console.debug(formatMessage("debug", message), ...args);
    }
  },

  /**
   * Info level - notable events and state changes.
   * Use for user actions, successful operations, milestones.
   */
  info: (message: string, ...args: unknown[]): void => {
    if (shouldLog("info")) {
      console.info(formatMessage("info", message), ...args);
    }
  },

  /**
   * Warn level - potential issues that don't prevent operation.
   * Use for deprecated features, fallbacks, recoverable errors.
   */
  warn: (message: string, ...args: unknown[]): void => {
    if (shouldLog("warn")) {
      console.warn(formatMessage("warn", message), ...args);
    }
  },

  /**
   * Error level - failures that impact functionality.
   * Use for caught exceptions, failed operations, critical issues.
   * Errors are automatically reported to Sentry (if configured).
   */
  error: (message: string, ...args: unknown[]): void => {
    if (shouldLog("error")) {
      console.error(formatMessage("error", message), ...args);

      // Report to Sentry
      reportToSentry(message, args);
    }
  },
};

export default logger;
